import React, { type ComponentProps, useEffect, useRef } from "react";
import { getFocusStyle } from "../../../theme";
import {
  css,
  isPropValid,
  styled,
  type StyleObject,
  useIsSmallScreenSize,
} from "../../../theme/styled";
import { useIsXsmallScreenSize } from "../../../theme/styled/designTokens/screenSize/screenSize";
import { zIndex } from "../../../theme/zIndex";
import { useDismiss } from "../../../util/useDismiss/useDismiss";
import { useMountTransition } from "../../../util/useMountTransition/useMountTransition";
import { useOnUnmount } from "../../../util/useOnUnmount/useOnUnmount";
import { Button } from "../../buttons/Button";
import { CrossIcon, type IconProps } from "../../icons/new-icons";
import { Drawer } from "../../popover/atoms/Drawer";
import { Heading } from "../../typography/Typography";
import { BackDrop } from "../../util/BackDrop";
import { FocusTrap } from "../../util/FocusTrap";
import { Margin } from "../../util/Margin";
import type { InteractionButtonsProps } from "./dialogTypes/InteractionButtons";

export interface InteractionDialogProps
  extends InteractionButtonsProps, DialogProps {}

export interface DialogProps {
  /**
   * Title of the dialog
   */
  title?: string;
  /**
   * Opens the modal
   */
  open: boolean;
  /**
   * Function to close the dialog
   */
  onClose: () => void;
  /**
   * Content of the dialog
   */
  children?: React.ReactNode;
  /**
   * A label for the close icon, for screen readers
   */
  ariaLabelCloseIcon: string;
  /**
   * A label for the modal, for screen readers
   */
  ariaLabel: string;
  /**
   * TestId of dialog
   */
  dataTestId?: string;
  /**
   * Reference to be focused on dismiss
   */
  onDismissFocusRef: React.RefObject<HTMLElement | null> | undefined;
  /**
   * Smaller icon placed in the top left corner of the dialog
   */
  icon?: React.FC<IconProps> | React.ReactNode;

  /**
   * Illustration placed in the top of the dialog.
   * Using this prop can trigger a React runtime error.
   * Use `Illustration` prop instead.
   */
  illustration?: React.ReactNode;
  /**
   * Illustration placed in the top of the dialog
   */
  Illustration?: () => React.JSX.Element;
  /*
   * Align illustration. Defaults to center.
   */
  alignIllustration?: "center" | "left";
  /**
   * Customize the width of the dialog. Defaults to "fixed".
   */
  width?: "fixed" | "stretched" | "fit" | number;
  /**
   * If the close X should be hidden
   */
  hideCloseButton?: boolean;
  /**
   * Defaults to `"center"`.
   */
  dialogAlignment?: "top" | "center" | "bottom";
  /**
   * Defaults to `"auto"`.
   */
  drawerSize?: "auto" | "full";
  /**
   * Icon placed in the confirm button
   */
  actionIcon?: NonNullable<ComponentProps<typeof Button.Icon>["iconComponent"]>;
}

type DialogBaseProps = DialogProps & {
  backgroundColor?: StyleObject["backgroundColor"];
};

export const DialogBase = ({
  children,
  title,
  onClose,
  open,
  ariaLabelCloseIcon,
  ariaLabel,
  dataTestId,
  icon,
  illustration,
  Illustration,
  width = "fixed",
  hideCloseButton,
  alignIllustration = "center",
  dialogAlignment = "center",
  drawerSize = "auto",
  backgroundColor,
}: DialogBaseProps): React.ReactNode => {
  const hasTransitionedIn = useMountTransition(open, 300);
  const isSmallScreen = useIsXsmallScreenSize();

  const dialogBackDropRef = useRef<HTMLDivElement | null>(null);
  const initialFocusRef = useRef<HTMLElement | null>(null);

  const initialBodyStyleOverflowRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      // Autofocus on the dialog element
      // – make sure it's focusable, e.g. using `tabIndex={-1}`
      initialFocusRef.current = document.querySelector<HTMLElement>(
        '[role="dialog"]',
      );
      initialFocusRef.current?.focus();

      // Save initial body overflow when Dialog is opened
      initialBodyStyleOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (initialBodyStyleOverflowRef.current !== null) {
      document.body.style.overflow = initialBodyStyleOverflowRef.current;
    }
  }, [open]);

  useOnUnmount(() => {
    if (initialBodyStyleOverflowRef.current !== null) {
      document.body.style.overflow = initialBodyStyleOverflowRef.current;
    }
  });

  useDismiss({
    handler: onClose,
    enabled: open,
    reference: dialogBackDropRef,
    escapeKey: true,
    closeOnClick: "inside",
  });

  return open || hasTransitionedIn
    ? (
      <FocusTrap initialFocusElementRef={initialFocusRef}>
        {isSmallScreen
          ? (
            <Drawer
              ariaLabel={ariaLabel}
              dataTestId={dataTestId}
              isOpen={open}
              onClose={onClose}
              disableUseDismiss={true}
              tabIndex={-1}
              size={drawerSize}
              data-appearance="drawer"
              backgroundColor={backgroundColor}
            >
              <DialogContent
                children={children}
                title={title}
                onClose={onClose}
                isSmallScreen
                ariaLabelCloseIcon={ariaLabelCloseIcon}
                dataComponentType="dialog"
                icon={icon}
                illustration={illustration}
                Illustration={Illustration}
                alignIllustration={alignIllustration}
                hideCloseButton={hideCloseButton}
              />
            </Drawer>
          )
          : (
            <>
              <DialogWrapper
                tabIndex={-1}
                role="dialog"
                aria-modal
                aria-label={ariaLabel}
                data-component-type="dialog"
                data-test-id={dataTestId}
                open={open}
                width={width}
                hasTransitionedIn={hasTransitionedIn}
                dialogAlignment={dialogAlignment}
                data-appearance="dialog"
                backgroundColor={backgroundColor}
              >
                <DialogContent
                  children={children}
                  title={title}
                  onClose={onClose}
                  ariaLabelCloseIcon={ariaLabelCloseIcon}
                  alignIllustration={alignIllustration}
                  icon={icon}
                  illustration={illustration}
                  Illustration={Illustration}
                  hideCloseButton={hideCloseButton}
                />
              </DialogWrapper>
              <BackDrop
                ref={dialogBackDropRef}
                visible={open}
                hasTransitionedIn={hasTransitionedIn}
              />
            </>
          )}
      </FocusTrap>
    )
    : null;
};

const DialogContent = ({
  onClose,
  title,
  children,
  ariaLabelCloseIcon,
  isSmallScreen,
  dataComponentType,
  icon,
  illustration,
  Illustration,
  hideCloseButton,
  alignIllustration = "center",
}:
  & Pick<
    DialogProps,
    | "onClose"
    | "children"
    | "title"
    | "ariaLabelCloseIcon"
    | "icon"
    | "illustration"
    | "Illustration"
    | "hideCloseButton"
    | "alignIllustration"
  >
  & {
    isSmallScreen?: boolean;
    dataComponentType?: string;
  }): React.JSX.Element => {
  const smallScreenSize = useIsSmallScreenSize();
  return (
    <div data-component-type={dataComponentType}>
      <div
        className={css({
          padding: isSmallScreen
            ? ["$spacing-16", "$spacing-20"]
            : "$spacing-0",
        })}
      >
        {icon &&
          (typeof icon === "function"
            ? (
              React.createElement(icon, {
                colorToken: "$text-primary",
                style: { marginBottom: "18" },
                size: 24,
              })
            )
            : <Margin bottom={18}>{icon}</Margin>)}
        {illustration && (
          <LargeIconWrapper align={alignIllustration}>
            {illustration}
          </LargeIconWrapper>
        )}
        {Illustration && (
          <LargeIconWrapper align={alignIllustration}>
            <Illustration />
          </LargeIconWrapper>
        )}
        {title && (
          <StyledHeading as="h2" size={smallScreenSize ? "medium" : "large"}>
            {title}
          </StyledHeading>
        )}
        {children}
      </div>
      {!hideCloseButton && (
        <CloseButtonWrapper>
          <Button
            aria-label={ariaLabelCloseIcon}
            variant="link"
            onClick={onClose}
            size="medium"
          >
            <Button.Icon iconComponent={CrossIcon} />
          </Button>
        </CloseButtonWrapper>
      )}
    </div>
  );
};

const StyledHeading = styled(Heading)({
  marginRight: "$spacing-16",
  marginBottom: "$spacing-8",
});

const LargeIconWrapper = styled("div")<{ align: "left" | "center" }>(
  ({ align }) => ({
    display: "flex",
    justifyContent: align,
    width: "100%",
    marginBottom: "$spacing-24",
  }),
);

const DialogWrapper = styled("div", {
  forwardProp: (prop) =>
    isPropValid(prop) && prop !== "open" && prop !== "bgColor",
})<
  & Required<
    Pick<DialogProps, "open" | "width" | "dialogAlignment">
  >
  & { backgroundColor?: StyleObject["backgroundColor"] }
  & {
    hasTransitionedIn: boolean;
  }
>((
  { open, width, dialogAlignment, hasTransitionedIn, backgroundColor },
) => {
  const getTransform = (): string => {
    const translateY = `translateY(${
      open && hasTransitionedIn ? "0px" : "10px"
    })`;

    if (dialogAlignment === "center") {
      return `translate(-50%,-50%) ${translateY}`;
    }
    return `translate(-50%) ${translateY}`;
  };

  return {
    opacity: open ? "1" : "0",
    display: "flex",
    backgroundColor: backgroundColor ?? "$background-tertiary",
    position: "fixed",
    left: "50%",
    top: dialogAlignment === "center"
      ? "50%"
      : dialogAlignment === "top"
      ? "1rem"
      : undefined,
    bottom: dialogAlignment === "bottom" ? "1rem" : undefined,
    transform: getTransform(),
    ...getWidth(width),
    maxHeight: "calc(100vh - $spacing-64)",
    borderRadius: "$radius-medium",
    boxShadow: "$shadow-raised",
    padding: "$spacing-32",
    flexDirection: "column",
    zIndex: zIndex.modal,
    transition: "transform 225ms ease-in-out, opacity 150ms ease-in-out",
    overflowX: "hidden",
    overflowY: "auto",
    ...getFocusStyle(),
  };
});

const CloseButtonWrapper = styled("div")({
  position: "absolute",
  top: 0,
  right: 0,
});

const getWidth = (
  width: DialogProps["width"],
): { width?: string; maxWidth?: string } => {
  if (typeof width === "number") {
    return { width: `${width}px` };
  }

  switch (width) {
    case "stretched":
      return {
        width: "calc(100vw - $spacing-64)",
        maxWidth: "1122px",
      };
    case "fit":
      return {
        maxWidth: "min(calc(100vw - $spacing-64), 1122px)",
      };
    case "fixed": // fallthrough
    default:
      return {
        width: "540px",
      };
  }
};
